import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, ArrowRight, Loader2, AlertCircle } from 'lucide-react';
import ProductCard from '../../components/ProductCard/ProductCard';
import { fetchProducts } from '../../utils/api';

export default function ProductCarousel({
  title      = 'New Arrivals',
  subtitle   = 'Fresh picks just arrived!',
  limit      = 8,
  categoryId = '',
  orderby    = 'date',
}) {
  const scrollRef = useRef(null);
  const [canLeft,  setCanLeft]  = useState(false);
  const [canRight, setCanRight] = useState(true);

  const { data: products = [], isLoading, isError } = useQuery({
    queryKey: ['carousel-products', categoryId, orderby, limit],
    queryFn:  () => {
      const params = { per_page: limit, orderby };
      if (orderby === 'price-desc') { params.orderby = 'price'; params.order = 'desc'; }
      if (categoryId) params.category = categoryId;
      return fetchProducts(params);
    },
    staleTime: 2 * 60_000,
  });

  function scroll(dir) {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 300, behavior: 'smooth' });
  }

  function onScroll() {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 0);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }

  const shopUrl = `${window.themeData?.siteUrl ?? ''}/shop/`;

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="flex items-end justify-between mb-10">
          <div>
            <h2 className="text-4xl md:text-5xl font-black text-textDark mb-2">{title}</h2>
            {subtitle && <p className="text-textLight text-lg">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => scroll(-1)}
              disabled={!canLeft}
              className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all ${canLeft ? 'border-primary text-primary hover:bg-primary hover:text-white' : 'border-gray-200 text-gray-300 cursor-not-allowed'}`}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => scroll(1)}
              disabled={!canRight}
              className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all ${canRight ? 'border-primary text-primary hover:bg-primary hover:text-white' : 'border-gray-200 text-gray-300 cursor-not-allowed'}`}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <a href={shopUrl} className="hidden md:flex items-center gap-1 text-primary font-bold hover:text-primaryHover transition-colors ml-2">
              View All <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-20 gap-3 text-primary">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span className="font-semibold text-textLight">Loading products…</span>
          </div>
        )}

        {isError && (
          <div className="flex items-center justify-center py-16 gap-3 text-red-400">
            <AlertCircle className="w-5 h-5" />
            <span className="font-semibold">Could not load products.</span>
          </div>
        )}

        {!isLoading && !isError && products.length === 0 && (
          <div className="text-center py-16 text-textLight">
            <p className="text-4xl mb-4">🛒</p>
            <p className="font-semibold">No products found.</p>
          </div>
        )}

        {!isLoading && !isError && products.length > 0 && (
          <div
            ref={scrollRef}
            onScroll={onScroll}
            className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide -mx-4 px-4"
          >
            {products.map((product, idx) => (
              <div key={product.id} className="flex-none w-72 snap-start">
                <ProductCard product={product} index={idx} />
              </div>
            ))}
          </div>
        )}

        <div className="text-center mt-8 md:hidden">
          <a href={shopUrl} className="inline-flex items-center gap-2 text-primary font-bold hover:text-primaryHover transition-colors">
            View All <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
